import { EmeraldError, EmeraldErrorSeverity } from 'src/common/api/emerald-error';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { restorePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { DeviceType } from 'src/common/enumerations/device-type';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import {
  PrintCheckEndorsementFailure,
  PrintCheckEndorsementRemoveTimeOut,
} from 'src/common/helpers/error-code';
import { IReceipt } from 'src/common/interfaces/dtos/IReceipt';
import { store } from 'src/common/redux/configure-store';
import { MICRActions } from 'src/common/redux/reducers/micr-reducer';
import { endorseCheck } from 'src/devices/printer-operations';
import { TenderingModeHomePage } from 'src/home-page/components/home-page-tendering';
import { makePaymentAndSetDefaultTender } from 'src/tendering/business-logic/handle-tender-payment';
import { showNotification } from 'src/tendering/business-logic/show-notification';
import { ITenderAdd } from '../../common/interfaces/dtos/IPreTenderAdd';
import { CheckEndorsementConfig, CheckSide } from '../../devices/micr-utilities';
import { CheckEndorsementViewProps } from '../components/check-processing-types';
import { showManualEndorsement } from '../components/manual-endorsement';
import { showPrintEndorsementView } from '../components/print-endorsement-view';
import { showMICRErrorModal } from '../components/micr-modal';
import { getEndorsementReceipts } from './handle-endorsement-receipts';
import { showEndorsementModal } from './handle-error-check-endorsement';

export const handlePrintEndorsement = async (
  lineTender: ITenderAdd,
  endorsementConfig: CheckEndorsementConfig,
): Promise<void> => {
  const endorseProps = {
    endorsementConfig,
    tenderDetails: lineTender,
    amountDue: store.getState().itemList.amountDue,
  };

  if (noEndorseConfig(endorsementConfig)) {
    goToManualEntryEndorsement(endorseProps);
    return;
  }

  const endorsementDocuments = await getEndorsementReceipts(lineTender)
    .then(receipts => receipts[0])
    .catch(() => undefined);
  if (!endorsementDocuments?.documents.length) {
    goToManualEntryEndorsement(endorseProps);
    return;
  }

  const { deviceList } = store.getState().touchPoint.touchPointConfiguration;
  const isDoubleSidePrinter =
    deviceList?.find(item => item.Type === DeviceType.Printer)?.DoubleSided === 'true';
  const needEndorseOtherSide = endorseBothSides(endorsementConfig) && !isDoubleSidePrinter;

  let checkSide = endorseOnlyBack(endorsementConfig) ? CheckSide.Back : CheckSide.Front;
  let attempts = 0;

  const onCancel = () => {
    store.dispatch(MICRActions.resetMICRData());
    restorePage(TenderingModeHomePage);
  };

  const onManualEndorse = () => {
    goToManualEntryEndorsement(endorseProps, checkSide === CheckSide.Back);
  };

  const onEndorse = async (): Promise<void> => {
    // TODO: Add this when doubleSide printing implemented
    // if(doublePrinter && endorseBothSides(endorsementConfig)){
    //   checkSide = CheckSide.Both;
    // }
    return withSpinner(() =>
      printEndorsement(checkSide, endorsementDocuments)
        .catch(async err => {
          if (err.code === PrintCheckEndorsementRemoveTimeOut) {
            // This error is the pritner notice to remove endorsed check
            // We can skip it when it does not block the flow, i.e. both sides endorsed
            if (checkSide === CheckSide.Front && needEndorseOtherSide) {
              await showNotification(
                'HeaderRemoveCheckEndorsement',
                'MICRRemoveTimeOut',
                EmeraldDialogTypes.Error,
              );
            }
          } else {
            await showEndorsementModal(endorseProps, checkSide === CheckSide.Back);
            throw err;
          }
        })
        .then(() => {
          if (checkSide === CheckSide.Front && needEndorseOtherSide) {
            checkSide = CheckSide.Back;
            throw new EmeraldError('EndorseOtherSide');
          }
          attempts++;
          return makeEndorsedCheckPayment(lineTender).catch(() => {
            // TODO this logic was copied from original code,
            // however, it does not make sense to do retry when SE decline a payment which is not related to check validation/edorsement
            // instead, it should close the page and display error message
            if (attempts > 3) {
              goToManualEntryEndorsement(endorseProps);
            } else {
              showMICRErrorModal({
                onRetry: () => {},
                onClose: onCancel,
              });
            }
          });
        }),
    );
  };

  showPrintEndorsementView({
    initialCheckSide: checkSide,
    onCancel,
    onManualEndorse,
    onEndorse,
  });
};

export const goToManualEntryEndorsement = async (
  endorseProps: CheckEndorsementViewProps,
  onlyShowBack?: boolean,
) => {
  const amountDue = store.getState().itemList.amountDue;
  const props = {
    amountDue: amountDue,
    tenderId: 'placeholder',
    screenData: endorseProps,
    onlyShowBack,
  };

  showManualEndorsement(props);
};

export const makeEndorsedCheckPayment = async (tenderDetails: ITenderAdd): Promise<void> => {
  const tenderAdd: ITenderAdd = { ...tenderDetails };
  tenderAdd.tender = {
    ...tenderDetails.tender,
    entryMethod: EntryMethod.Automatic,
    endorsementPrinted: true,
  };
  await makePaymentAndSetDefaultTender(tenderAdd)
    .catch(err => {
      if (!(err instanceof AbortedError && err.code === 'ProcessCheck')) {
        throw err;
      }
    })
    .finally(() => store.dispatch(MICRActions.resetMICRData()));
};

const printEndorsement = (checkSide: CheckSide, endorsementDocuments: IReceipt): Promise<void> => {
  const [front, back] = endorsementDocuments.documents;
  // TODO print double side
  // if (checkSide === CheckSide.Both && front && back){
  //   return endorseCheck({documents: [front, back]});
  // }
  if (checkSide === CheckSide.Front && front) {
    return endorseCheck({ documents: [front] });
  }
  if (checkSide === CheckSide.Back && back) {
    return endorseCheck({ documents: [back] });
  }
  throw new EmeraldError(
    PrintCheckEndorsementFailure,
    'Problem with endorsement documents',
    EmeraldErrorSeverity.Error,
    { checkSide, endorsementDocuments },
  );
};

export const endorseOnlyFront = (config: CheckEndorsementConfig) => {
  return hasFrontReceiptLine(config) && !hasBackReceiptLine(config);
};

export const endorseOnlyBack = (config: CheckEndorsementConfig) => {
  return !hasFrontReceiptLine(config) && hasBackReceiptLine(config);
};

export const endorseBothSides = (config: CheckEndorsementConfig) => {
  return hasFrontReceiptLine(config) && hasBackReceiptLine(config);
};

export const noEndorseConfig = (config: CheckEndorsementConfig) => {
  return !hasFrontReceiptLine(config) && !hasBackReceiptLine(config);
};

const hasFrontReceiptLine = (config: CheckEndorsementConfig) => {
  return !!config.receiptLinesFront?.length;
};

const hasBackReceiptLine = (config: CheckEndorsementConfig) => {
  return !!config.receiptLinesBack?.length;
};
